/*
Component to run searches on past queries from user search history
to see if there are any new results or better matches.

Searches are run periodically (check every 30 min, run once per query per day)
and only on queries that have not had a notification shown yet.
*/

import { useEffect } from "react";
import useFirebaseAuthentication from "../../common/hooks/useFirebaseAuthentication";
import { sendAuthenticatedRequest } from "../../common/functions";
import { Product } from "../../common/models/product";
import { getMismatchType, MISMATCH_GRADE } from "./helpers";
import { Link } from "react-router-dom";
import { isEqual } from "lodash";
import { Timestamp } from "firebase/firestore";
import { useNotifications } from "../../common/stateDefs";
import { SearchHistoryItem } from "../../buyerStateDefs";

export const PastQuerySearch = () => {
  const { authUser } = useFirebaseAuthentication();
  const addNotification = useNotifications((state) => state.addNotification);

  let intervalId: NodeJS.Timeout;

  useEffect(() => {
    if (authUser) {
      if (intervalId) {
        clearInterval(intervalId);
      }
      return () => {
        clearInterval(intervalId);
      };
    }
    // if no interval exists yet, run immediately
    runPastQuerySearches();

    // create an interval to run past query searches periodically
    intervalId = setInterval(
      () => {
        runPastQuerySearches();
      },
      30 * 60 * 1000,
    ); // every 30 minutes

    return () => {
      clearInterval(intervalId);
    };
  }, [authUser]);

  const secondsWithinLast24h = (seconds: number) => {
    return Date.now() / 1000 - seconds < 24 * 60 * 60;
  };

  const runPastQuerySearches = async () => {
    if (authUser) return;

    const searchHistory = JSON.parse(
      localStorage.getItem("searchHistoryWithoutSignIn") || "[]",
    ) as SearchHistoryItem[];

    const queriesToIgnore = new Set<string>();
    for (const item of searchHistory) {
      if (
        item.improvementNotificationShown ||
        (item.lastCheckedForNewResults &&
          secondsWithinLast24h(item.lastCheckedForNewResults.seconds)) ||
        item.minMismatchGrade === 0
      ) {
        queriesToIgnore.add(item.query);
      }
    }

    for (const item of searchHistory) {
      if (queriesToIgnore.has(item.query)) {
        continue;
      }
      // run search for this query
      const searchResponse = await sendAuthenticatedRequest(
        process.env.REACT_APP_SEARCH_JS_API_BASE_URL || "",
        {
          method: "POST",
          jsonBody: {
            query: item.query,
            appliedFilters: item.appliedFilters || [],
            backgroundCheck: true,
          },
        },
      ).then((resp) => {
        console.log(
          "Background search response retrieved for query ",
          item.query,
          resp,
        );
        return resp.json();
      });

      // process searchResponse to check for new or better results
      // if found, show notification to user
      const products: Product[] = searchResponse.results.map(
        (r: any) => r._source as Product,
      );
      const structuredQuery = searchResponse.structure;

      if (products.length === 0) {
        // no results, skip
        continue;
      }

      const newMinMismatchGrade = Math.min(
        ...products
          .map((p) => getMismatchType(p, structuredQuery))
          .map((mt) => MISMATCH_GRADE[mt as keyof typeof MISMATCH_GRADE]),
      );

      let notificationSent = false;

      if (
        item.minMismatchGrade === undefined ||
        (newMinMismatchGrade < 20 &&
          newMinMismatchGrade < item.minMismatchGrade)
      ) {
        // found better match, show notification
        console.log("Found better match for query ", item.query);
        const newNotification = {
          id: `improvement-${item.query}-${Date.now()}`,
          type: "info" as const,
          title: "New Inventory Alert",
          content: (
            <>
              <p>
                New and better matching inventory found for your past search
                query: <i>{item.query}</i>
              </p>
              <p>
                Click <Link to={`/${item.urlQueryString}`}>here</Link> to view
                the new results.
              </p>
            </>
          ),
        };

        addNotification(newNotification);
        notificationSent = true;
      } else {
        console.log("No better match found for query ", item.query);
      }
      // update query history in local storage
      // we read from local storage again to avoid overwriting changes
      const updatedSearchHistory = JSON.parse(
        localStorage.getItem("searchHistoryWithoutSignIn") || "[]",
      ) as SearchHistoryItem[];
      for (let shi of updatedSearchHistory) {
        if (isEqual(shi, item)) {
          shi.improvementNotificationShown = notificationSent;
          shi.lastCheckedForNewResults = Timestamp.now();
        }
      }
      localStorage.setItem(
        "searchHistoryWithoutSignIn",
        JSON.stringify(updatedSearchHistory),
      );
    }
  };

  return null;
};
